import { DndContext, closestCenter, PointerSensor, useSensors, useSensor, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Info, Plus } from 'lucide-react';
import { useState } from 'react';

import { cn } from '~/shadcn/lib/utils';

import { useFormControls, useFormState } from '../providers/FormStateProvider';
import { SLICE_REGISTRY } from '../../utils/constants';

import AddSliceDialog from './AddSliceDialog';

/**
 * SliceMinimap is a component that provides a draggable, miniature view of content slices.
 *
 * It allows users to:
 * - View all content slices in a compact format
 * - Reorder slices through drag and drop
 * - Add new slices at any position
 * - Highlight and select individual slices
 *
 * @returns {JSX.Element} A minimap interface for managing content slices
 */
function SliceMinimap() {
    const { formData } = useFormState();
    const { addSlice, clearHighlightedSlice, highlightSlice, highlightedSliceId, loadingServerData, moveSlice } =
        useFormControls();

    const [activeId, setActiveId] = useState(null);
    const [addSliceDialogState, setAddSliceDialogState] = useState({ isOpen: false, position: null });

    const sensors = useSensors(useSensor(PointerSensor));

    const handleDragStart = (e) => {
        setActiveId(e.active.id);
        clearHighlightedSlice();
    };

    const handleDragEnd = (e) => {
        const { active, over } = e;

        if (active.id !== over.id) {
            const oldIndex = formData.slices.findIndex((slice) => slice.id === active.id);
            const newIndex = formData.slices.findIndex((slice) => slice.id === over.id);
            moveSlice(oldIndex, newIndex);
        }

        setActiveId(null);
    };

    const handleDragCancel = () => {
        setActiveId(null);
    };

    const handleAddSliceDialogClose = () => {
        setAddSliceDialogState((prev) => ({ ...prev, isOpen: false }));
    };

    const handleAddSliceDialogOpen = ({ position }) => {
        setAddSliceDialogState({ isOpen: true, position });
    };

    const handleAddSliceDialogSelect = (sliceType) => {
        addSlice(sliceType, addSliceDialogState.position);
        setAddSliceDialogState((prev) => ({ ...prev, isOpen: false }));
    };

    return (
        <>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                <SortableContext items={formData.slices} strategy={verticalListSortingStrategy}>
                    {/* Placeholder to occupy fixed-position space */}
                    <div className="shrink-0 w-100" />

                    <div className="fixed top-34 bottom-0 w-100 overflow-y-auto">
                        <div
                            className={cn(
                                'flex flex-col gap-6 pt-4 pr-6 pb-16 pl-1',
                                activeId && '[&_.add-slice-button]:opacity-0'
                            )}
                        >
                            <div
                                className={cn(
                                    'grid grid-cols-[1fr_auto_1fr] gap-2 rounded-md p-4 font-medium text-xs bg-gray-100 hover:bg-border hover:cursor-pointer transition-all duration-200 ease-in-out',
                                    highlightedSliceId === 'metadata' && 'bg-gray-200 outline outline-ring shadow-md'
                                )}
                                onClick={() => highlightSlice('metadata')}
                            >
                                <Info className="size-4" /> Metadata
                            </div>

                            {formData.slices.length === 0 && (
                                <div className="flex flex-col gap-6">
                                    <AddSliceButton
                                        disabled={loadingServerData}
                                        onAddSliceClick={() => handleAddSliceDialogOpen({ position: 0 })}
                                    />
                                </div>
                            )}
                            {formData.slices?.map((slice, index) => (
                                <DraggableSlice
                                    key={slice.id}
                                    index={index}
                                    slice={slice}
                                    loadingServerData={loadingServerData}
                                    overlayPlaceholder={activeId === slice.id}
                                    highlighted={activeId !== slice.id && highlightedSliceId === slice.id}
                                    onAddSliceClick={handleAddSliceDialogOpen}
                                    onSliceClick={() => highlightSlice(slice.id)}
                                />
                            ))}
                            <DragOverlay>
                                {activeId && (
                                    <DraggableSlice
                                        overlay
                                        handleAddSliceDialogOpen={() => {}}
                                        index={formData.slices.findIndex((slice) => slice.id === activeId)}
                                        slice={formData.slices.find((slice) => slice.id === activeId)}
                                    />
                                )}
                            </DragOverlay>
                        </div>
                    </div>
                </SortableContext>
            </DndContext>
            <AddSliceDialog
                isOpen={addSliceDialogState.isOpen}
                onClose={handleAddSliceDialogClose}
                onSelect={handleAddSliceDialogSelect}
                position={addSliceDialogState.position}
            />
        </>
    );
}

/**
 * A draggable slice component that represents a content slice in the minimap.
 *
 * @param {Object} props
 * @param {number} props.index - The position of the slice in the list
 * @param {boolean} props.highlighted - Whether the slice is currently highlighted
 * @param {boolean} props.loadingServerData - Whether the server data is loading
 * @param {Function} props.onAddSliceClick - Callback for when the add slice button is clicked
 * @param {Function} props.onSliceClick - Callback for when the slice itself is clicked
 * @param {boolean} props.overlay - Whether this slice is being rendered as a drag overlay
 * @param {boolean} props.overlayPlaceholder - Whether this slice should be rendered as a placeholder during drag
 * @param {Object} props.slice - The slice data object containing id, type, and other properties
 * @returns {JSX.Element} A draggable slice component
 */
function DraggableSlice({
    index,
    highlighted,
    loadingServerData,
    onAddSliceClick,
    onSliceClick,
    overlay,
    overlayPlaceholder,
    slice,
}) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: slice.id,
        disabled: loadingServerData,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const displayName = slice.name || SLICE_REGISTRY[slice.displayType]?.displayName || '';
    const thumbnail = SLICE_REGISTRY[slice.displayType]?.thumbnail || '';

    return (
        <div className="flex flex-col gap-6">
            {index === 0 && !overlay && (
                <AddSliceButton disabled={loadingServerData} onAddSliceClick={() => onAddSliceClick({ position: 0 })} />
            )}
            <div
                className={cn(
                    'flex flex-col gap-2 w-full bg-gray-100 rounded-md p-3 pr-0 hover:cursor-pointer hover:bg-border',
                    (highlighted || overlay) && 'bg-gray-200 outline outline-ring shadow-md',
                    overlayPlaceholder && 'opacity-[0.5]'
                )}
                onClick={() => onSliceClick(slice.id)}
                ref={setNodeRef}
                style={style}
            >
                <div className="flex items-center gap-2">
                    <p>{index + 1}</p>
                    <div className="shrink-1 p-2 rounded-md bg-background">
                        <img src={thumbnail} alt={slice.displayType} className="object-contain" />
                    </div>
                    <div
                        className={cn(
                            'self-stretch flex items-center justify-center pr-3 cursor-grab',
                            loadingServerData && 'opacity-50 cursor-not-allowed'
                        )}
                        {...attributes}
                        {...listeners}
                    >
                        <GripVertical className="size-4" />
                    </div>
                </div>
                <p className="text-xs font-medium">{displayName}</p>
            </div>
            {!overlay && (
                <AddSliceButton
                    disabled={loadingServerData}
                    onAddSliceClick={() => onAddSliceClick({ position: index + 1 })}
                />
            )}
        </div>
    );
}

/**
 * A button that allows adding new slices between existing slices.
 *
 * @param {Object} props
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {Function} props.onAddSliceClick - Callback function triggered when the add button is clicked
 * @returns {JSX.Element} A button for adding new slices
 */
function AddSliceButton({ disabled, onAddSliceClick }) {
    return (
        <div className="add-slice-button relative w-full h-[1px] bg-border transition-opacity duration-200 ease-in-out">
            <button
                className={cn(
                    'absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center bg-gray-300 outline-4 outline-[#F6F6F6] rounded-full text-[#F6F6F6] transition-all duration-200 ease-in-out hover:cursor-pointer hover:bg-gray-400',
                    disabled && 'bg-gray-200 hover:cursor-not-allowed hover:bg-gray-200'
                )}
                onClick={onAddSliceClick}
                type="button"
                disabled={disabled}
            >
                <Plus className="size-4" />
            </button>
        </div>
    );
}

export default SliceMinimap;
